import { AdyenUtils } from '@core/services/system/adyen/adyen-utils';
import { Device } from '@core/capacitor-plugins/capacitor-plugins.class';
import { Injectable } from '@angular/core';
import { MANUFACTURER_TYPES } from '@shared/constants/manufacturer-types';
import { AdyenDeviceModel } from '@shared/constants';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { StorageKeys } from '@shared/constants/storage.const';
import { TerminalNames } from '@shared/constants/terminal-names.enum';
import { AdyenTerminalInfo } from '@core/services/system/adyen/classes';

@Injectable()
export abstract class IAdyenService {
  abstract get isPrinterConnected(): boolean;
  abstract get isScannerConnected(): boolean;
  abstract get isAdyenDevice(): boolean;
  abstract get adyenDeviceModel(): string;
  abstract get isAdyenTerminalConnected(): boolean;
}

@Injectable()
export class AdyenService extends IAdyenService {
  private readonly modelsWithPrinter = ['S1F2'];
  private readonly modelsWithScanner = [];
  private _isAdyenDevice = false;
  private _deviceModel: AdyenDeviceModel;

  public constructor(private readonly adyenUtils: AdyenUtils, private localStorageService: LocalStorage) {
    super();
    this.init().then();
  }

  get isAdyenDevice(): boolean {
    return this._isAdyenDevice && !!this._deviceModel;
  }

  get adyenDeviceModel() {
    return this._deviceModel;
  }

  get isPrinterConnected(): boolean {
    const terminal: AdyenTerminalInfo = this.adyenUtils.getTerminal();
    if (!terminal) return false;
    return this.modelsWithPrinter.includes(terminal.deviceModel);
  }

  get isScannerConnected(): boolean {
    const terminal: AdyenTerminalInfo = this.adyenUtils.getTerminal();
    if (!terminal) return false;
    return this.modelsWithScanner.includes(terminal.deviceModel);
  }

  get isAdyenTerminalConnected(): boolean {
    const activeTerminal = this.localStorageService.getObject(StorageKeys.activeTerminal);
    return [TerminalNames.PAYMASH_PAY, TerminalNames.SOFTPOS].includes(activeTerminal.name);
  }

  private async init(): Promise<void> {
    this._isAdyenDevice = await this.isAdyenManufacturer();
    this._deviceModel = await this.detectDeviceModel();
  }

  private async isAdyenManufacturer(): Promise<boolean> {
    const { manufacturer } = await this.getDeviceInfo();
    const adyenManufacturers = [MANUFACTURER_TYPES.QUALCOMM, MANUFACTURER_TYPES.CASTLES];
    return adyenManufacturers.some((deviceManufacturer) => deviceManufacturer === manufacturer);
  }

  private async detectDeviceModel(): Promise<AdyenDeviceModel> {
    let model = (await this.getDeviceInfo())?.model || '';
    model = model.toLocaleLowerCase();
    return Object.values(AdyenDeviceModel).find((deviceModel) => model.includes(deviceModel.toLocaleLowerCase()));
  }

  protected async getDeviceInfo() {
    return Device.getInfo();
  }
}
